import { forwardRef, useId, type ChangeEvent, type InputHTMLAttributes, type ReactNode } from 'react';
import { aplicarEmailDigitado } from '@/shared/lib/normalizarEmail';
import styles from './Input.module.css';

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & {
  label?: string;
  error?: string;
  helperText?: string;
  className?: string;
  trailing?: ReactNode;
};

/**
 * Campo de texto. `required` e apenas visual (asterisco vermelho + aria-required);
 * nao aplica o atributo HTML required — os forms usam noValidate + RHF/Zod.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, helperText, className, id, required, type, onChange, trailing, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = error ? `${inputId}-error` : undefined;
  const email = type === 'email';

  const aoMudar = (e: ChangeEvent<HTMLInputElement>) => {
    if (email) aplicarEmailDigitado(e);
    onChange?.(e);
  };

  const campo = (
    <input
      ref={ref}
      id={inputId}
      className={`${styles.input} ${error ? styles.inputError : ''}`}
      aria-invalid={Boolean(error)}
      aria-describedby={errorId}
      aria-required={required || undefined}
      {...rest}
      type={type}
      inputMode={email ? 'email' : rest.inputMode}
      autoCapitalize={email ? 'none' : rest.autoCapitalize}
      autoCorrect={email ? 'off' : rest.autoCorrect}
      spellCheck={email ? false : rest.spellCheck}
      onChange={aoMudar}
    />
  );

  return (
    <div className={`${styles.field} ${className ?? ''}`}>
      {label ? (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required ? (
            <>
              {' '}
              <span className={styles.required} aria-hidden="true">
                *
              </span>
            </>
          ) : null}
        </label>
      ) : null}
      {trailing ? (
        <div className={styles.withTrailing}>
          {campo}
          {trailing}
        </div>
      ) : (
        campo
      )}
      {error ? (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      ) : helperText ? (
        <span className={styles.helper}>{helperText}</span>
      ) : null}
    </div>
  );
});
